import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CheckCircle2, ImageOff, Loader2, Star, Truck, UploadCloud, X } from 'lucide-react';
import { Decimal, REVIEW_ASPECT_TAGS, REVIEW_ASPECT_TAG_LABEL, REVIEW_MAX_PHOTOS } from '@mlm/shared';
import { PaymentInstructions } from '../components/payment-instructions';
import { CopyButton } from '../components/copy-button';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  Input,
  Skeleton,
  Separator,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  cn,
} from '@mlm/ui';
import {
  ecomPaymentMethodQrUrl,
  getDragonpayChannels,
  getEcomPaymentMethods,
  getOrder,
  getOrderGroup,
  initiateGateway,
  listOrderReviews,
  markOrderReceived,
  performGatewayHandoff,
  productImageUrl,
  submitProductReview,
  uploadManualPayment,
  uploadManualPaymentGroup,
  type EcomPaymentMethodOption,
  type OrderDetail,
  type ProductReview,
} from '../lib/api';
import { fmtCurrency, fmtDateTime } from '../lib/format';

export function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const q = useQuery({
    queryKey: ['order', id],
    queryFn: () => getOrder(id!),
    enabled: !!id,
    // Poll while awaiting payment so an online-gateway order flips to `paid`
    // shortly after the verified webhook lands (the buyer may return from the
    // gateway a beat before processing commits). Stops once it leaves to_pay.
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s === 'pending' || s === 'to_pay' ? 3000 : false;
    },
  });

  // Mixed-cart single payment: when this order belongs to a checkout group with
  // siblings, the page shows EVERY order in the group (you pay for all at once),
  // not just the one you opened.
  const groupId = q.data?.checkoutGroupId ?? null;
  const groupQ = useQuery({
    queryKey: ['order-group', groupId],
    queryFn: () => getOrderGroup(groupId!),
    enabled: !!groupId,
  });
  const isGroup = !!groupQ.data && groupQ.data.orders.length > 1;

  // Awaiting-payment orders get a "how to pay" procedure panel in the page's
  // right margin on desktop (so long steps have room and don't crowd the upload
  // form); on mobile it's a tab inside the form instead.
  const awaitingPayment = !!q.data && (q.data.status === 'pending' || q.data.status === 'to_pay');

  return (
    <div className="space-y-4">
      <Link to="/orders" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
        <ArrowLeft className="h-4 w-4" /> Back to orders
      </Link>
      <div className="lg:flex lg:items-start lg:gap-6">
        <div className="space-y-4 lg:min-w-0 lg:flex-1 lg:max-w-3xl">
      {q.isLoading ? (
        <Skeleton className="h-48" />
      ) : !q.data ? (
        <p className="text-sm text-destructive">Order not found.</p>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Order Ref. Number
                </div>
                <CardTitle className="font-mono">{q.data.code}</CardTitle>
                <div className="text-xs text-muted-foreground mt-1">
                  {fmtDateTime(q.data.createdAt)} · {q.data.type}
                </div>
              </div>
              <Badge variant="outline">{q.data.status}</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {isGroup ? (
              <GroupItemsSection groupId={groupId!} />
            ) : (
            <>
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground border-b">
                <tr>
                  <th className="py-2 font-medium">Item</th>
                  <th className="py-2 font-medium text-right">Qty</th>
                  <th className="py-2 font-medium text-right">Unit</th>
                  <th className="py-2 font-medium text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {q.data.items.map((it, idx) => (
                  <tr key={idx} className="border-b last:border-b-0">
                    <td className="py-2">
                      <div className="flex items-center gap-2">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded border bg-muted">
                          {it.productImages && it.productImages.length > 0 ? (
                            <img
                              src={productImageUrl(it.productId, 0)}
                              alt=""
                              className="h-full w-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <ImageOff className="h-4 w-4 text-muted-foreground" aria-hidden />
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="truncate font-medium">
                            {it.productName ?? `${it.productId.slice(0, 8)}…`}
                            {it.variantName ? ` — ${it.variantName}` : ''}
                          </div>
                          {it.productSku && (
                            <div className="truncate font-mono text-xs text-muted-foreground">
                              {it.productSku}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-2 text-right">{it.qty}</td>
                    <td className="py-2 text-right tabular-nums">
                      {fmtCurrency(it.unitPrice)}
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      {fmtCurrency((Number(it.unitPrice) * it.qty).toFixed(4))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <Separator />

            <div className="space-y-1 text-sm">
              <Row label="Subtotal" value={fmtCurrency(q.data.subtotal)} />
              {Number(q.data.discountTotal) > 0 && (
                <Row
                  label="Discount"
                  value={`−${fmtCurrency(q.data.discountTotal)}`}
                  className="text-credit"
                />
              )}
              {Number(q.data.shippingFee) > 0 && (
                <Row label="Shipping" value={fmtCurrency(q.data.shippingFee)} />
              )}
              <Row label="Total" value={fmtCurrency(q.data.total)} bold />
              {Number(q.data.totalPv) > 0 && (
                <Row label="Total PV" value={Number(q.data.totalPv).toFixed(0)} muted />
              )}
            </div>
            </>
            )}

            {(q.data.courierId || q.data.shipToAddress || q.data.shipFromBranch) && (
              <div className="rounded-md border p-3 text-xs space-y-1">
                <p className="font-medium">
                  {q.data.fulfillmentMode === 'branch_pickup' ? 'Pickup' : 'Shipping'}
                </p>
                {q.data.shipFromBranch && (
                  <p className="text-muted-foreground">
                    {q.data.fulfillmentMode === 'branch_pickup' ? 'Pickup at' : 'Ships from'}:{' '}
                    <span className="text-foreground">{q.data.shipFromBranch.name}</span>{' '}
                    <span className="font-mono">({q.data.shipFromBranch.code})</span>
                  </p>
                )}
                {q.data.courierId && (
                  <p className="text-muted-foreground">Courier: {q.data.courierId}</p>
                )}
                {q.data.shipToAddress && (
                  <p className="text-muted-foreground">
                    Deliver to: {formatAddress(q.data.shipToAddress)}
                  </p>
                )}
              </div>
            )}

            <Separator />

            <div>
              <p className="text-sm font-medium mb-2">Order timeline</p>
              <ol className="space-y-2">
                {(q.data.statusHistory ?? []).map((h, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-sm">
                    <span className="mt-1 h-2 w-2 rounded-full bg-primary shrink-0" />
                    <div>
                      <span className="capitalize font-medium">{h.status.replace(/_/g, ' ')}</span>
                      <span className="text-xs text-muted-foreground ml-2">
                        {fmtDateTime(h.changedAt)}
                      </span>
                      {h.note && <div className="text-xs text-muted-foreground">{h.note}</div>}
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            {(q.data.trackingNumber || q.data.trackingUrl) && (
              <div className="rounded-md border p-3 text-xs space-y-1">
                {q.data.trackingNumber && (
                  <div className="flex items-center gap-1">
                    <span className="text-muted-foreground">Tracking #: </span>
                    <span className="font-mono">{q.data.trackingNumber}</span>
                    <CopyButton value={q.data.trackingNumber} label="Tracking number copied" title="Copy tracking number" />
                  </div>
                )}
                {q.data.trackingUrl && (
                  <Button asChild size="sm" variant="outline" className="mt-1 h-8 gap-1.5">
                    <a href={q.data.trackingUrl} target="_blank" rel="noopener noreferrer">
                      <Truck className="h-4 w-4" aria-hidden />
                      Track Package
                    </a>
                  </Button>
                )}
              </div>
            )}

            {(q.data.status === 'pending' || q.data.status === 'to_pay') && (
              <>
                <GatewayPaymentPanel order={q.data} />
                <ManualPaymentForm order={q.data} />
              </>
            )}

            {(q.data.status === 'delivered' || q.data.status === 'to_pick') && (
              <MarkReceivedCta order={q.data} />
            )}

            {q.data.status === 'completed' && (
              <ReviewsSection order={q.data} />
            )}
          </CardContent>
        </Card>
      )}
        </div>

        {awaitingPayment && q.data && (
          <aside className="hidden lg:sticky lg:top-6 lg:block lg:w-80 lg:shrink-0">
            <PaymentProcedureAside order={q.data} />
          </aside>
        )}
      </div>
    </div>
  );
}

/**
 * All orders in a single-payment checkout group, each as its own shop section
 * (items + per-order total) plus a combined total. Fetches every sibling order's
 * detail (the current one is already cached under the same ['order', id] key, so
 * it dedupes). Shown in place of the single-order item table when the order
 * belongs to a multi-order group.
 */
function GroupItemsSection({ groupId }: { groupId: string }) {
  const groupQ = useQuery({
    queryKey: ['order-group', groupId],
    queryFn: () => getOrderGroup(groupId),
  });
  const ids = groupQ.data?.orders.map((o) => o.id) ?? [];
  const detailQs = useQueries({
    queries: ids.map((oid) => ({
      queryKey: ['order', oid],
      queryFn: () => getOrder(oid),
    })),
  });

  if (groupQ.isLoading || detailQs.some((dq) => dq.isLoading)) {
    return <Skeleton className="h-40" />;
  }
  const details = detailQs.map((dq) => dq.data).filter((d): d is OrderDetail => !!d);
  const grand = details.reduce((s, d) => s.plus(new Decimal(d.total)), new Decimal(0));

  return (
    <div className="space-y-3">
      <p className="text-sm font-medium">This single payment covers {details.length} orders</p>
      {details.map((d) => (
        <div key={d.id} className="overflow-hidden rounded-md border">
          <div className="flex items-center justify-between gap-2 border-b bg-muted/30 px-3 py-2 text-xs">
            <span className="font-medium">{d.shipFromBranch?.name ?? 'Store'}</span>
            <Link to={`/orders/${d.id}`} className="font-mono text-muted-foreground hover:text-foreground">
              {d.code}
            </Link>
          </div>
          <table className="w-full text-sm">
            <tbody>
              {d.items.map((it, idx) => (
                <tr key={idx} className="border-b last:border-0">
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded border bg-muted">
                        {it.productImages && it.productImages.length > 0 ? (
                          <img
                            src={productImageUrl(it.productId, 0)}
                            alt=""
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <ImageOff className="h-4 w-4 text-muted-foreground" aria-hidden />
                        )}
                      </div>
                      <span className="truncate">
                        {it.productName ?? `${it.productId.slice(0, 8)}…`}
                        {it.variantName ? ` — ${it.variantName}` : ''}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">×{it.qty}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {fmtCurrency(new Decimal(it.unitPrice).times(it.qty).toFixed(2))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex justify-between border-t px-3 py-2 text-sm">
            <span className="text-muted-foreground">Order total</span>
            <span className="font-medium tabular-nums">{fmtCurrency(d.total)}</span>
          </div>
        </div>
      ))}
      <div className="flex justify-between text-sm font-semibold">
        <span>Combined total</span>
        <span className="tabular-nums">{fmtCurrency(grand.toFixed(2))}</span>
      </div>
    </div>
  );
}

function MarkReceivedCta({ order }: { order: OrderDetail }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const mut = useMutation({
    meta: { successMessage: 'Order marked received.' },
    mutationFn: () => markOrderReceived(order.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['order', order.id] });
      qc.invalidateQueries({ queryKey: ['orders'] });
      // Move the order out of "to receive" into "to rate" on the sidebar badge.
      qc.invalidateQueries({ queryKey: ['orders', 'action-summary'] });
      setOpen(false);
      setErrMsg(null);
    },
    onError: (err: unknown) => {
      const data = (err as { response?: { data?: { error?: string; message?: string } } }).response?.data;
      setErrMsg(data?.message ?? data?.error ?? 'Failed to mark received');
    },
  });
  return (
    <div className="rounded-md border border-primary/30 bg-primary/5 p-3 text-sm space-y-2">
      <p className="font-medium">
        {order.status === 'delivered' ? 'Did you receive your order?' : 'Did you pick up your order?'}
      </p>
      <p className="text-xs text-muted-foreground">
        Marking received closes out the order and unlocks product reviews.
      </p>
      <Button size="sm" onClick={() => setOpen(true)}>
        <CheckCircle2 className="h-4 w-4" /> Mark received
      </Button>
      <ConfirmDialog
        open={open}
        onOpenChange={(o) => { if (!o && !mut.isPending) { setOpen(false); setErrMsg(null); } }}
        title="Mark order as received?"
        description={
          <>
            Confirming will move the order to <span className="font-mono">completed</span>. You'll
            be able to leave product reviews afterward.
          </>
        }
        confirmLabel="Yes, I received it"
        errorMessage={errMsg}
        loading={mut.isPending}
        onConfirm={async () => { await mut.mutateAsync(); }}
      />
    </div>
  );
}

function ReviewsSection({ order }: { order: OrderDetail }) {
  const reviewsQ = useQuery({
    queryKey: ['order-reviews', order.id],
    queryFn: () => listOrderReviews(order.id),
  });
  if (reviewsQ.isLoading) return <Skeleton className="h-32" />;
  const reviewsByItem = new Map<string, ProductReview>();
  for (const r of reviewsQ.data?.reviews ?? []) reviewsByItem.set(r.orderItemId, r);

  return (
    <div className="space-y-3">
      <p className="text-sm font-medium">Reviews</p>
      <p className="text-xs text-muted-foreground">
        Rate each item — add photos, pick what stood out, and write a comment so other members can
        decide whether to buy.
      </p>
      <ul className="space-y-3">
        {order.items.map((it) => {
          const existing = reviewsByItem.get(it.id);
          return (
            <li key={it.id}>
              {existing ? (
                <ReviewSummary review={existing} />
              ) : (
                <ReviewForm
                  orderId={order.id}
                  orderItemId={it.id}
                  productId={it.productId}
                  productName={it.productName ?? null}
                  variantName={it.variantName ?? null}
                />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ReviewSummary({ review }: { review: ProductReview }) {
  const photoCount = review.photoR2Keys?.length ?? (review.photoR2Key ? 1 : 0);
  return (
    <div className="rounded-md border p-3 text-sm space-y-1.5">
      <div className="flex items-center gap-2">
        <RatingStars value={review.rating} readOnly />
        <span className="text-xs text-muted-foreground">{fmtDateTime(review.createdAt)}</span>
      </div>
      {review.aspectTags && review.aspectTags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {review.aspectTags.map((t) => (
            <span key={t} className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
              {REVIEW_ASPECT_TAG_LABEL[t] ?? t}
            </span>
          ))}
        </div>
      )}
      {review.comment && <p className="text-sm">{review.comment}</p>}
      {photoCount > 0 && (
        <p className="text-xs text-muted-foreground">
          {photoCount} photo{photoCount > 1 ? 's' : ''} attached
        </p>
      )}
    </div>
  );
}

function ReviewForm({
  orderId,
  orderItemId,
  productId,
  productName,
  variantName,
}: {
  orderId: string;
  orderItemId: string;
  productId: string;
  productName?: string | null;
  variantName?: string | null;
}) {
  const qc = useQueryClient();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const [tags, setTags] = useState<string[]>([]);

  function addPhotos(files: FileList | null) {
    if (!files) return;
    setPhotos((prev) => [...prev, ...Array.from(files)].slice(0, REVIEW_MAX_PHOTOS));
  }
  function removePhoto(i: number) {
    setPhotos((prev) => prev.filter((_, idx) => idx !== i));
  }
  function toggleTag(code: string) {
    setTags((prev) => (prev.includes(code) ? prev.filter((t) => t !== code) : [...prev, code]));
  }

  const mut = useMutation({
    meta: { successMessage: 'Review submitted.' },
    mutationFn: () =>
      submitProductReview({
        orderId,
        orderItemId,
        rating,
        comment: comment || undefined,
        photos,
        aspectTags: tags,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['order-reviews', orderId] });
      // Refresh the sidebar "to rate" badge once this item is reviewed.
      qc.invalidateQueries({ queryKey: ['orders', 'action-summary'] });
    },
  });

  return (
    <div className="rounded-md border p-3 space-y-2.5">
      <p className="text-sm font-medium">
        {productName ?? `${productId.slice(0, 8)}…`}
        {variantName ? ` — ${variantName}` : ''}
      </p>
      <RatingStars value={rating} onChange={setRating} />

      {/* Aspect-tag chips */}
      <div className="flex flex-wrap gap-1.5">
        {REVIEW_ASPECT_TAGS.map((t) => {
          const active = tags.includes(t.code);
          return (
            <button
              key={t.code}
              type="button"
              onClick={() => toggleTag(t.code)}
              className={cn(
                'rounded-full border px-2.5 py-1 text-[11px] transition-colors',
                active
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-input text-muted-foreground hover:border-primary',
              )}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="What did you think of this product?"
        className="w-full min-h-[80px] rounded-md border bg-background p-2 text-sm"
        maxLength={2000}
      />

      {/* Photos (up to REVIEW_MAX_PHOTOS) */}
      <div className="space-y-1.5">
        <label className="text-xs text-muted-foreground">
          Photos (optional, up to {REVIEW_MAX_PHOTOS})
        </label>
        {photos.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {photos.map((f, i) => (
              <div key={i} className="relative h-16 w-16 overflow-hidden rounded border">
                <img src={URL.createObjectURL(f)} alt="" className="h-full w-full object-cover" />
                <button
                  type="button"
                  onClick={() => removePhoto(i)}
                  className="absolute right-0.5 top-0.5 rounded-full bg-black/60 p-0.5 text-white"
                  aria-label="Remove photo"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        {photos.length < REVIEW_MAX_PHOTOS && (
          <Input type="file" accept="image/*" multiple onChange={(e) => addPhotos(e.target.files)} />
        )}
      </div>

      {mut.error && (
        <p className="text-xs text-destructive">
          {(mut.error as { response?: { data?: { error?: string; message?: string } } }).response
            ?.data?.message ?? 'Failed to submit review.'}
        </p>
      )}
      <Button size="sm" onClick={() => mut.mutate()} disabled={rating < 1 || mut.isPending}>
        {mut.isPending ? 'Submitting…' : 'Submit review'}
      </Button>
    </div>
  );
}

function RatingStars({
  value,
  onChange,
  readOnly,
}: {
  value: number;
  onChange?: (n: number) => void;
  readOnly?: boolean;
}) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={readOnly}
          onClick={() => onChange?.(n)}
          className="text-amber-500 disabled:cursor-default"
          aria-label={`${n} star${n > 1 ? 's' : ''}`}
        >
          <Star className={`h-5 w-5 ${n <= value ? 'fill-current' : ''}`} />
        </button>
      ))}
    </div>
  );
}

/**
 * Online-gateway (Dragonpay / iPay88) payment panel for an awaiting-payment
 * order. The webhook is the source of truth, so this panel polls (via the page
 * query's `refetchInterval`) and just offers a "Pay" / "Pay again" button that
 * re-initiates the gateway handoff. When the buyer returns from the gateway
 * (`?pay=<provider>` in the URL) it shows a "Confirming…" state until the
 * verified webhook flips the order to `paid`.
 */
function GatewayPaymentPanel({ order }: { order: OrderDetail }) {
  const methodCode =
    (order.metadata as Record<string, unknown> | null | undefined)?.payment_method_code ?? null;
  const methodsQ = useQuery({
    queryKey: ['ecom-payment-methods'],
    queryFn: () => getEcomPaymentMethods(),
    enabled: !!methodCode,
  });
  const method = methodCode
    ? methodsQ.data?.methods.find((m) => m.code === methodCode) ?? null
    : null;
  const [params] = useSearchParams();
  const returned = !!params.get('pay'); // browser came back from the gateway
  const groupId = order.checkoutGroupId ?? null;
  const isDragonpay = method?.gatewayProvider === 'dragonpay';

  // Dragonpay v2 collect REQUIRES a channel (ProcId) — let the buyer pick one
  // from the merchant's enabled list (GCash, cards, banks). iPay88/stub don't.
  const [procId, setProcId] = useState('');
  const channelsQ = useQuery({
    queryKey: ['dragonpay-channels'],
    queryFn: getDragonpayChannels,
    enabled: !!method && method.settlement === 'gateway' && isDragonpay,
    staleTime: 60 * 60 * 1000,
  });

  const mut = useMutation({
    mutationFn: async () => {
      const { handoff } = await initiateGateway({
        ...(groupId ? { checkoutGroupId: groupId } : { orderId: order.id }),
        ...(procId ? { procId } : {}),
      });
      performGatewayHandoff(handoff); // browser leaves the SPA to pay
    },
  });

  if (!method || method.settlement !== 'gateway') return null;

  const needsChannel = isDragonpay; // buyer must pick a channel for Dragonpay
  const canPay = !needsChannel || !!procId;

  return (
    <div className="rounded-md border p-4 space-y-3">
      {returned && (
        <div className="flex items-center gap-2 text-sm">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          <span>Confirming your payment… this page updates automatically once it clears.</span>
        </div>
      )}
      <p className="text-sm text-muted-foreground">
        Pay securely with <span className="font-medium text-foreground">{method.label}</span>.
        {needsChannel ? ' Choose how you want to pay:' : " You'll be taken to the payment page and sent back here when you're done."}
      </p>
      {needsChannel && (
        <select
          className="h-9 w-full rounded-md border bg-background px-2 text-sm"
          value={procId}
          onChange={(e) => setProcId(e.target.value)}
          disabled={channelsQ.isLoading}
        >
          <option value="">{channelsQ.isLoading ? 'Loading channels…' : 'Select a payment channel…'}</option>
          {(channelsQ.data ?? []).map((c) => (
            <option key={c.procId} value={c.procId}>
              {c.name}
            </option>
          ))}
        </select>
      )}
      <Button onClick={() => mut.mutate()} disabled={mut.isPending || !canPay}>
        {mut.isPending ? 'Redirecting…' : returned ? 'Pay again' : 'Pay now'}
      </Button>
      {mut.isError && (
        <p className="text-xs text-destructive">Couldn't start the payment. Please try again.</p>
      )}
      {channelsQ.isError && needsChannel && (
        <p className="text-xs text-destructive">Couldn't load payment channels. Please try again.</p>
      )}
    </div>
  );
}

/**
 * Proof-of-payment upload for orders settled via a `settlement='manual'`
 * payment method. We look up the method the buyer chose at checkout from the
 * order's `metadata.payment_method_code`, fetch its `accountLabel`/`instructions`
 * from the registry, and render the form. Wallet-settled orders or orders
 * without a registry code (legacy / public-checkout) just show a generic
 * awaiting-payment notice with no upload.
 */
function ManualPaymentForm({ order }: { order: OrderDetail }) {
  const qc = useQueryClient();
  const methodCode =
    (order.metadata as Record<string, unknown> | null | undefined)?.payment_method_code ?? null;
  const methodsQ = useQuery({
    queryKey: ['ecom-payment-methods'],
    queryFn: () => getEcomPaymentMethods(),
    enabled: !!methodCode,
  });
  const method: EcomPaymentMethodOption | null = methodCode
    ? methodsQ.data?.methods.find((m) => m.code === methodCode) ?? null
    : null;
  // Durable label fallback: the order stamps `payment_method_label` at creation,
  // so even if the registry entry `methodCode` pointed at was renamed/removed/
  // disabled, we still have a human label to show + submit. This is what keeps
  // the proof form alive — the old gate hid the form whenever `method` was null
  // (the reported "manual pay never asked for a photo" bug).
  const metaLabel = (order.metadata as Record<string, unknown> | null | undefined)
    ?.payment_method_label as string | undefined;
  const methodLabelText =
    method?.label ??
    metaLabel ??
    (typeof methodCode === 'string' ? methodCode : undefined) ??
    'Manual payment';
  const isCod =
    (order.metadata as Record<string, unknown> | null | undefined)?.cod_manual === true ||
    method?.settlement === 'cod_manual';

  // Single-payment: when this order is part of a mixed-cart checkout group, the
  // buyer pays ONCE for every order in the group (one proof, summed total).
  const groupId = order.checkoutGroupId ?? null;
  const groupQ = useQuery({
    queryKey: ['order-group', groupId],
    queryFn: () => getOrderGroup(groupId!),
    enabled: !!groupId,
  });
  const group = groupQ.data ?? null;
  const isGroupPay = !!group && group.orders.length > 1 && !group.allPaid;
  const payAmount = isGroupPay ? group!.payableTotal : order.total;

  const [reference, setReference] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const mut = useMutation({
    meta: { successMessage: 'Proof of payment submitted.' },
    mutationFn: async () => {
      if (!file) throw new Error('missing_fields');
      // The `currency` column on manual_payments is an ISO 4217 code (the money
      // actually paid). Manual settlement is cash-only (enforced by the admin
      // registry validator), so this is always the tenant's base currency — PHP
      // for the only deployment so far. If multi-region tenants ship later,
      // plumb base_currency through /me or the order.
      if (isGroupPay) {
        return uploadManualPaymentGroup({
          checkoutGroupId: groupId!,
          method: methodLabelText,
          amount: payAmount,
          currency: 'PHP',
          reference: reference || undefined,
          file,
        });
      }
      return uploadManualPayment({
        orderId: order.id,
        method: methodLabelText,
        amount: order.total,
        currency: 'PHP',
        reference: reference || undefined,
        file,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['order', order.id] });
      if (groupId) qc.invalidateQueries({ queryKey: ['order-group', groupId] });
    },
  });

  // Online-gateway orders are handled by `GatewayPaymentPanel`; yield here.
  if (method?.settlement === 'gateway') return null;
  // Wallet-settled orders already completed at checkout, and COD collects cash
  // on delivery — neither uploads a proof. (Both normally land in a non-pending
  // status, so this branch is defensive.) Show the passive notice.
  if (method?.settlement === 'wallet' || isCod) {
    return (
      <div className="rounded-md border border-warning/30 bg-warning/10 p-3 text-xs">
        <p className="font-medium text-warning">Awaiting payment</p>
        <p className="text-warning/90 mt-1">
          Once admin approves the payment, this order completes and any kit codes drop into your
          Code Vault.
        </p>
      </div>
    );
  }
  // Everything else awaiting payment is a manual cash order → render the proof
  // upload form below. This INCLUDES orders whose method can't be resolved from
  // the live registry (code null, or the method was renamed/removed/disabled
  // after the order was placed): the form must NOT disappear, or the buyer can
  // never pay and the admin has nothing to approve (the reported bug).

  if (mut.data) {
    return (
      <div className="rounded-md border border-success/30 bg-success/10 p-3 text-xs">
        <p className="font-medium text-success">Proof submitted</p>
        <p className="text-success/90 mt-1">
          We received your proof of payment. Admin will review it shortly; you'll see the order
          flip to <span className="font-mono">paid</span> once approved.
        </p>
      </div>
    );
  }

  // Item 6: the step-by-step "How to pay" procedure moves to its own panel on
  // the RIGHT (a separate tab on mobile), so the upload form on the left isn't
  // pushed down by a long instructions block. On `lg` the two sit side by side;
  // on narrow screens they become tabs.
  // Admin-uploaded scannable payment QR (e.g. GCash / Maya). Shown prominently
  // above the instructions so the buyer can scan to pay.
  const qrBlock = method?.qrKey ? (
    <div className="rounded-md border p-4 text-center">
      <p className="text-sm font-medium">Scan to pay with {method.label}</p>
      <img
        src={ecomPaymentMethodQrUrl(method.code)}
        alt={`${method.label} payment QR`}
        className="mx-auto mt-2 h-56 w-56 rounded border bg-white object-contain"
      />
      <p className="mt-2 text-xs text-muted-foreground">
        Scan with your payment app, then upload your receipt below.
      </p>
    </div>
  ) : null;

  const procedure = (
    <div className="space-y-3">
      {qrBlock}
      {method?.instructions ? (
        <PaymentInstructions
          methodLabel={method.label}
          markdown={method.instructions}
          accountLabel={method.accountLabel}
        />
      ) : (
        <div className="rounded-md border p-4 text-sm">
          <p className="font-medium">Pay with {methodLabelText}</p>
          {method?.accountLabel && (
            <p className="mt-1 text-muted-foreground">Send to: {method.accountLabel}</p>
          )}
        </div>
      )}
    </div>
  );

  // If a prior proof was rejected, the order was returned to `to_pay` with a
  // history note `payment rejected: <reason>`. Surface the latest one so the
  // member knows why and can re-upload.
  const rejectedNote = [...(order.statusHistory ?? [])]
    .reverse()
    .find((h) => h.status === 'to_pay' && (h.note ?? '').startsWith('payment rejected'));
  const rejectedReason = rejectedNote
    ? rejectedNote.note!.replace(/^payment rejected:\s*/, '').trim()
    : null;

  const uploadForm = (
    <div className="rounded-md border p-4 space-y-3">
      {rejectedNote && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2.5 text-xs">
          <p className="font-medium text-destructive">Previous proof rejected</p>
          <p className="mt-0.5 text-destructive/90">
            {rejectedReason
              ? `Reason: ${rejectedReason}. `
              : ''}
            Please upload a new proof of payment. Use the reference number from your
            new payment.
          </p>
        </div>
      )}
      {isGroupPay && (
        <div className="rounded-md border border-primary/30 bg-primary/5 p-2.5 text-xs">
          <p className="font-medium">Single payment for {group!.orders.length} orders</p>
          <p className="mt-0.5 text-muted-foreground">
            This pays for every shop in your purchase at once. Amount payable:{' '}
            <b className="text-foreground">{fmtCurrency(payAmount)}</b>
          </p>
        </div>
      )}
      <div>
        <label className="text-xs text-muted-foreground">Reference / receipt number</label>
        <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. 1234567890" />
      </div>

      <div>
        <label className="text-xs text-muted-foreground">Proof of payment (image or PDF)</label>
        <Input
          type="file"
          accept="image/*,application/pdf"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
        {file && (
          <p className="mt-1 text-xs text-muted-foreground">
            {file.name} · {(file.size / 1024).toFixed(0)} KB
          </p>
        )}
      </div>

      {mut.error && (
        <p className="text-xs text-destructive">{prettyUploadError(mut.error)}</p>
      )}

      <Button
        onClick={() => mut.mutate()}
        disabled={!file || mut.isPending}
        size="sm"
        className="w-full"
      >
        <UploadCloud className="h-4 w-4" />
        {mut.isPending ? 'Uploading…' : 'Submit proof of payment'}
      </Button>
    </div>
  );

  return (
    <>
      {/* Narrow screens: tabs so the procedure is a separate tab, not a long
          scroll above the form (no right-margin aside exists on mobile). */}
      <div className="lg:hidden">
        <Tabs defaultValue="pay">
          <TabsList className="w-full">
            <TabsTrigger value="pay" className="flex-1">Submit payment</TabsTrigger>
            <TabsTrigger value="how" className="flex-1">How to pay</TabsTrigger>
          </TabsList>
          <TabsContent value="pay" className="mt-3">{uploadForm}</TabsContent>
          <TabsContent value="how" className="mt-3">{procedure}</TabsContent>
        </Tabs>
      </div>

      {/* Wide screens: just the form — the step-by-step procedure is rendered in
          the page's right margin by PaymentProcedureAside. */}
      <div className="hidden lg:block">{uploadForm}</div>
    </>
  );
}

/**
 * Desktop-only "How to pay" panel rendered in the order page's right margin so a
 * long step-by-step list has room without crowding the upload form. Resolves the
 * same manual method the form uses (shared query); renders nothing for
 * wallet-settled / code-less orders.
 */
function PaymentProcedureAside({ order }: { order: OrderDetail }) {
  const methodCode =
    (order.metadata as Record<string, unknown> | null | undefined)?.payment_method_code ?? null;
  const methodsQ = useQuery({
    queryKey: ['ecom-payment-methods'],
    queryFn: () => getEcomPaymentMethods(),
    enabled: !!methodCode,
  });
  const method: EcomPaymentMethodOption | null = methodCode
    ? methodsQ.data?.methods.find((m) => m.code === methodCode) ?? null
    : null;
  if (!method || method.settlement !== 'manual') return null;
  return (
    <div>
      <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Step-by-step
      </p>
      {method.qrKey && (
        <div className="mb-3 rounded-md border p-4 text-center">
          <p className="text-sm font-medium">Scan to pay with {method.label}</p>
          <img
            src={ecomPaymentMethodQrUrl(method.code)}
            alt={`${method.label} payment QR`}
            className="mx-auto mt-2 h-56 w-56 rounded border bg-white object-contain"
          />
        </div>
      )}
      {method.instructions ? (
        <PaymentInstructions
          methodLabel={method.label}
          markdown={method.instructions}
          accountLabel={method.accountLabel}
        />
      ) : (
        <div className="rounded-md border p-4 text-sm">
          <p className="font-medium">Pay with {method.label}</p>
          {method.accountLabel && (
            <p className="mt-1 text-muted-foreground">Send to: {method.accountLabel}</p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Surface the API's actual rejection reason. The /payments/manual route can
 * fail for several distinct reasons (file too big, malformed currency, order
 * not in a payable status, ClamAV-infected). The generic "Upload failed"
 * message hides the cause; this peels the axios envelope so the user knows
 * what to fix.
 */
function prettyUploadError(err: unknown): string {
  const data = (err as { response?: { data?: { error?: string; issues?: Array<{ message?: string; path?: unknown[] }> } } })
    .response?.data;
  const code = data?.error;
  switch (code) {
    case 'order_not_found':
      return 'Order not found.';
    case 'order_not_payable':
      return 'This order is no longer accepting payment.';
    case 'file_too_large':
      return 'File too large. Use an image or PDF under 10 MB.';
    case 'unsupported_file_type':
      return 'Unsupported file type. Upload an image (JPG/PNG) or a PDF.';
    case 'infected_file':
      return 'The file failed the virus scan. Try a different copy of the receipt.';
    case 'duplicate_reference':
      return 'That reference number has already been submitted for this payment method. Double-check the number or try a different one.';
    case 'amount_mismatch':
      return 'The payment amount no longer matches your order total. Refresh the page and try again.';
    case 'group_not_payable':
      return 'These orders are no longer accepting payment.';
    case 'validation_error': {
      const first = data?.issues?.[0]?.message;
      return first ? `Invalid input: ${first}` : 'Invalid input. Check the form and try again.';
    }
    default:
      return 'Upload failed. Check the file (image or PDF under 10 MB) and try again.';
  }
}

function formatAddress(a: Record<string, string | undefined>): string {
  return [a.line1, a.line2, a.barangay, a.city, a.province, a.region, a.postalCode]
    .filter(Boolean)
    .join(', ');
}

function Row({
  label,
  value,
  bold,
  muted,
  className,
}: {
  label: string;
  value: string;
  bold?: boolean;
  muted?: boolean;
  className?: string;
}) {
  return (
    <div
      className={`flex justify-between ${bold ? 'font-semibold' : ''} ${
        muted ? 'text-muted-foreground' : ''
      } ${className ?? ''}`}
    >
      <span>{label}</span>
      <span className="tabular-nums font-mono">{value}</span>
    </div>
  );
}
